import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { Form, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import {
  RadioButtonGroup,
  RadioButtonItem,
} from '@/components/RadioButtonGroup'
import { pluralize } from '@/utils/pluralization-util'
import { type CapacityType } from '@/features/user-management/ManageUserCapacityDialog/ManageUserCapacityDialogViewModel'

export interface ManageUserCapacityDialogProps {
  showing: boolean
  onClose: () => void
  capacityType: CapacityType
  onCapacityTypeChange: (type: CapacityType) => void
  /**
   * Whether both global and workspace capacity can be edited.
   */
  canChooseCapacityType: boolean
  /**
   * The single user being edited, or `null` when editing a selection.
   */
  selectedUserName: string | null
  selectedUserCount: number
  workspaceName: string
  onSubmit: () => void
  isSubmitPending: boolean
  /**
   * The capacity amount field.
   */
  children: ReactNode
}

export function ManageUserCapacityDialog({
  showing,
  onClose,
  capacityType,
  onCapacityTypeChange,
  canChooseCapacityType,
  selectedUserName,
  selectedUserCount,
  workspaceName,
  onSubmit,
  isSubmitPending,
  children,
}: ManageUserCapacityDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Manage {capacityType === 'Global' ? 'global' : 'workspace'} capacity
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            Adjust the amount of active concurrent jobs{' '}
            <b>
              {selectedUserName ??
                `${selectedUserCount} ${pluralize('user', selectedUserCount)}`}
            </b>{' '}
            can have {capacityType === 'Global' ? 'globally' : 'in'}
            <b>{capacityType === 'Workspace' ? ` ${workspaceName}` : ''}</b>.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form onSubmit={onSubmit}>
            {canChooseCapacityType && (
              <FormRow>
                <FormGroupContainer>
                  <RadioButtonGroup size="big-wide">
                    <RadioButtonItem
                      testId="user-capacity-global-button"
                      onPress={() => onCapacityTypeChange('Global')}
                      active={capacityType === 'Global'}
                    >
                      Global
                    </RadioButtonItem>
                    <RadioButtonItem
                      testId="user-capacity-workspace-button"
                      onPress={() => onCapacityTypeChange('Workspace')}
                      active={capacityType === 'Workspace'}
                    >
                      Workspace
                    </RadioButtonItem>
                  </RadioButtonGroup>
                </FormGroupContainer>
              </FormRow>
            )}

            <FormRow>{children}</FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label="Save"
            variant="primary"
            testId="manage-user-capacity-submit-button"
            onPress={onSubmit}
            isDisabled={isSubmitPending}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
